import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Search, MessageCircle, Heart, ShieldCheck, LifeBuoy, ArrowRight, Quote, Check,
} from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import { TravelSearchWidget } from "@/components/TravelSearchWidget";
import { SectionTitle } from "@/components/SectionTitle";
import { CTASection } from "@/components/CTASection";
import { MascotHelpSection } from "@/components/MascotHelpSection";
import { FAQAccordion } from "@/components/FAQAccordion";
import { Reveal } from "@/components/Reveal";
import {
  ASSETS, CATEGORIES, TRUST_ITEMS, HERO_BADGES, FAQ_CATEGORIES, waLink, WA_MESSAGES,
} from "@/lib/site";

const ICONS = { Heart, ShieldCheck, Search, LifeBuoy };

const homeFaq = FAQ_CATEGORIES.flatMap((c) => c.items).slice(0, 5);

export default function Home() {
  useSeo({
    title: "Miríade Turismo | Agência de Viagens em São José do Rio Preto",
    description:
      "Viaje com segurança, praticidade e atendimento humano. Encontre sua próxima viagem com a Miríade Turismo e conte com uma equipe pronta para ajudar você.",
  });

  return (
    <>
      {/* 1. HERO */}
      <section data-testid="home-hero" className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={ASSETS.heroCouple} alt="Casal viajando ao pôr do sol" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-dark/95 via-brand/80 to-brand/40" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6 sm:py-28 lg:px-8 lg:py-36">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="max-w-2xl"
          >
            <span className="overline !text-brand-lilac">Sua próxima viagem começa aqui</span>
            <h1 className="mt-4 font-heading text-4xl font-bold leading-[1.1] tracking-tight text-white sm:text-5xl lg:text-6xl">
              Viaje com segurança, praticidade e atendimento humano
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-white/80">
              Encontre sua próxima viagem com a Miríade Turismo e conte com uma equipe pronta para
              ajudar você antes, durante e depois da sua experiência.
            </p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/buscar-viagens"
                data-testid="hero-search-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-4 text-base font-semibold text-brand transition-all hover:scale-[1.03] hover:bg-brand-light"
              >
                <Search size={20} /> Buscar minha viagem
              </Link>
              <a
                href={waLink(WA_MESSAGES.default)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-8 py-4 text-base font-semibold text-white transition-all hover:scale-[1.03] hover:bg-[#1ebe5d]"
              >
                <MessageCircle size={20} /> Falar com a Miríade
              </a>
            </div>
            <ul className="mt-10 grid max-w-xl grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
              {HERO_BADGES.map((b) => (
                <li key={b} className="flex items-center gap-2.5 text-sm font-medium text-white/90">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white/20">
                    <Check size={13} />
                  </span>
                  {b}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* 2. SEARCH */}
      <section data-testid="home-search" className="relative px-4 pb-16 pt-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl">
          <SectionTitle
            center
            overline="Encontre sua próxima viagem"
            title="Encontre sua próxima viagem"
            subtitle="Pesquise destinos, datas e opções de viagem com praticidade. E, se precisar de ajuda, conte com o atendimento da Miríade Turismo."
          />
          <div className="mt-10">
            <TravelSearchWidget />
          </div>
        </div>
      </section>

      {/* 3. WHY CHOOSE */}
      <section data-testid="home-trust" className="px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <SectionTitle
            center
            overline="Por que a Miríade"
            title="Por que escolher a Miríade Turismo"
            subtitle="Tecnologia para pesquisar com praticidade e pessoas de verdade para cuidar de cada detalhe."
          />
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {TRUST_ITEMS.map((item, i) => {
              const Icon = ICONS[item.icon];
              const bgs = ["bg-brand-light", "bg-sky", "bg-blush", "bg-brand-lilac/30"];
              return (
                <Reveal key={item.title} delay={i * 0.08}>
                  <div
                    data-testid={`trust-card-${i}`}
                    className="h-full rounded-3xl border border-border bg-white p-8 shadow-soft transition-all hover:-translate-y-1 hover:shadow-hover"
                  >
                    <div className={`flex h-14 w-14 items-center justify-center rounded-2xl ${bgs[i % 4]}`}>
                      <Icon size={26} className="text-brand" />
                    </div>
                    <h3 className="mt-6 font-heading text-lg font-semibold text-ink">{item.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-ink-soft">{item.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* 4. CATEGORIES */}
      <section data-testid="home-categories" className="px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <SectionTitle
            center
            overline="Inspire-se"
            title="Escolha o tipo de viagem que combina com você"
            subtitle="Clique no estilo de viagem desejado e fale com a Miríade para receber orientação personalizada."
          />
          <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-4">
            {CATEGORIES.map((cat, i) => (
              <Reveal key={cat.title} delay={(i % 4) * 0.06}>
                <a
                  href={waLink(cat.wa)}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${cat.cta} pelo WhatsApp`}
                  data-testid={`category-card-${i}`}
                  className="group block cursor-pointer"
                >
                  <div className="relative h-56 overflow-hidden rounded-3xl shadow-soft transition-all duration-300 group-hover:-translate-y-1 group-hover:shadow-hover sm:h-64">
                  <img
                    src={cat.image}
                    alt={cat.title}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/95 via-ink/45 to-ink/5" />
                  <div className="absolute inset-x-0 bottom-0 p-5">
                    <h3 className="font-heading text-lg font-semibold text-white">{cat.title}</h3>
                    <p className="mt-1 text-xs text-white/75">{cat.desc}</p>
                    <span className="mt-3 inline-flex max-w-full items-center gap-1 rounded-full border border-white/25 bg-white/15 px-3 py-1.5 text-[11px] font-semibold text-white shadow-sm backdrop-blur-md transition-all duration-300 group-hover:border-brand-lilac/70 group-hover:bg-white/25 sm:text-xs">
                      <MessageCircle size={13} className="shrink-0" />
                      <span className="min-w-0 whitespace-normal leading-tight">{cat.cta}</span>
                    </span>
                  </div>
                  </div>
                </a>
              </Reveal>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="font-heading text-lg font-semibold text-ink">Prefere pesquisar por conta própria?</p>
            <Link
              to="/buscar-viagens"
              data-testid="categories-search-alternative"
              className="mt-4 inline-flex items-center justify-center gap-2 rounded-full border border-brand/30 bg-white px-7 py-3.5 text-sm font-semibold text-brand shadow-soft transition-all hover:scale-[1.02] hover:bg-brand-light hover:shadow-hover"
            >
              <Search size={18} /> Buscar viagens
            </Link>
          </div>
        </div>
      </section>

      {/* 5. CEO */}
      <section data-testid="home-ceo" className="px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl">
          <div className="grid items-center gap-10 rounded-3xl border border-border bg-white p-6 shadow-premium sm:p-10 lg:grid-cols-2 lg:gap-16 lg:p-12">
            <Reveal>
              <div className="w-full max-w-[25rem]">
                <div className="relative">
                  <div className="absolute -left-4 -top-4 h-24 w-24 rounded-3xl bg-brand-light" />
                  <div className="absolute -bottom-4 -right-4 h-28 w-28 rounded-3xl bg-blush" />
                  <img
                    src="/images/mirian-pellizon.jpeg"
                    alt="Mirian Ledo, CEO da Miríade Turismo"
                    className="relative aspect-[5/6] w-full rounded-3xl object-cover object-[center_38%] shadow-soft"
                  />
                </div>
                <div className="mt-5 text-left">
                  <strong className="block font-heading text-xl font-semibold text-ink">Mirian Ledo</strong>
                  <span className="mt-1 block text-sm font-medium text-brand">CEO da Miríade Turismo</span>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div>
                <span className="overline">Atendimento humano</span>
                <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-ink sm:text-4xl">
                  Atendimento humano para você viajar com mais segurança
                </h2>
                <p className="mt-5 text-base leading-relaxed text-ink-soft">
                  Na Miríade Turismo, você pode pesquisar sua viagem online com praticidade, mas também
                  conta com uma equipe pronta para orientar sua escolha, tirar dúvidas e acompanhar cada
                  detalhe da sua experiência.
                </p>
                <div className="mt-7 rounded-2xl border-l-4 border-brand bg-brand-light/50 p-5">
                  <Quote size={22} className="text-brand" />
                  <p className="mt-2 font-heading text-lg font-medium italic text-ink">
                    “Viajar é uma decisão importante. Nosso compromisso é ajudar você a escolher com
                    tranquilidade, segurança e cuidado.”
                  </p>
                </div>
                <Link
                  to="/conheca"
                  data-testid="ceo-know-more-btn"
                  className="mt-7 inline-flex items-center gap-2 rounded-full border border-brand/30 bg-white px-6 py-3 text-sm font-semibold text-brand transition-all hover:bg-brand-light"
                >
                  Conheça a Miríade <ArrowRight size={16} />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 6. MASCOT + WHATSAPP */}
      <MascotHelpSection />

      {/* 7. FAQ SUMMARY */}
      <section data-testid="home-faq" className="px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl">
          <SectionTitle center overline="Dúvidas frequentes" title="Perguntas que ajudam a decidir" />
          <div className="mt-8">
            <FAQAccordion categories={homeFaq} single />
          </div>
          <div className="mt-8 text-center">
            <Link
              to="/perguntas-frequentes"
              data-testid="faq-see-all-btn"
              className="inline-flex items-center gap-2 rounded-full bg-brand px-7 py-3.5 text-sm font-semibold text-white transition-all hover:scale-[1.02] hover:bg-brand-hover"
            >
              Ver todas as dúvidas <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* 8. FINAL CTA */}
      <CTASection />
    </>
  );
}
